import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { useAgency } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import { describe, generateNotifications } from '../lib/notifications';
import Icon from './Icon';

// Zvonce sa obaveštenjima (gornja traka aplikacije)
const REFRESH_MS = 30 * 60 * 1000;

function timeAgo(iso, tr) {
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 3600) return tr('upravo sada', 'just now', 'только что');
  if (diff < 86400) { const h = Math.floor(diff / 3600); return tr(`pre ${h} h`, `${h} h ago`, `${h} ч назад`); }
  const d = Math.floor(diff / 86400);
  return tr(`pre ${d} d`, `${d} d ago`, `${d} дн. назад`);
}

export default function NotificationBell() {
  const { user } = useAuth();
  const { agencies, activeAgencyId, selectAgency } = useAgency();
  const { lang, tr } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await supabase.from('notifications').select('*').order('created_at', { ascending: false }).limit(50);
    setItems(data || []);
  }, []);

  // Pri otvaranju aplikacije i na svakih 30 minuta: izračunaj nova obaveštenja, pa učitaj listu
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try { await generateNotifications(user, agencies); } catch { /* ne ruši aplikaciju */ }
      if (!cancelled) load();
    };
    run();
    const timer = setInterval(run, REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, agencies.length]);

  // Zatvori listu klikom van nje
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    document.addEventListener('keydown', onKey);
    // Dok je lista otvorena, na telefonu se sakriva dugme AI asistenta (da ne pokriva listu)
    document.body.classList.add('notif-open');
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('notif-open');
    };
  }, [open]);

  // "15.10.2026.." → "15.10.2026." (datum se već završava tačkom)
  const tidy = (txt) => String(txt || '').replace(/\.\.(?=\s|$)/g, '.');

  const unread = items.filter((n) => !n.read_at).length;

  const markAllRead = async () => {
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: now })));
    await supabase.from('notifications').update({ read_at: now }).is('read_at', null);
  };

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read_at) {
      const now = new Date().toISOString();
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read_at: now } : x)));
      supabase.from('notifications').update({ read_at: now }).eq('id', n.id);
    }
    if (n.agency_id && n.agency_id !== activeAgencyId && selectAgency) selectAgency(n.agency_id);
    if (n.link) setTimeout(() => navigate(n.link), 0);
  };

  const ICONS = { tax_deadline: 'calendar', invoice_overdue: 'receipt', app_news: 'star' };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={styles.bellBtn}
        aria-label={tr('Obaveštenja', 'Notifications', 'Уведомления')}
        title={tr('Obaveštenja', 'Notifications', 'Уведомления')}
      >
        <Icon name="bell" size={19} />
        {unread > 0 && <span style={styles.badge}>{unread > 9 ? '9+' : unread}</span>}
      </button>

      {open && (
        <div style={styles.panel} className="notif-panel" role="dialog" aria-label={tr('Obaveštenja', 'Notifications', 'Уведомления')}>
          <div style={styles.panelHead}>
            <strong>{tr('Obaveštenja', 'Notifications', 'Уведомления')}</strong>
            {unread > 0 && (
              <button onClick={markAllRead} style={styles.linkBtn}>{tr('Označi sve kao pročitano', 'Mark all as read', 'Отметить всё как прочитанное')}</button>
            )}
          </div>
          <div style={styles.list} className="notif-list">
            {items.length === 0 && (
              <div style={styles.empty}>{tr('Nema obaveštenja.', 'No notifications.', 'Нет уведомлений.')}</div>
            )}
            {items.map((n) => {
              const d = describe(n, lang);
              const title = tidy(d.title);
              const body = d.body ? tidy(d.body) : '';
              const icon = ICONS[n.type] || 'chart';
              return (
                <button key={n.id} onClick={() => openItem(n)} style={{ ...styles.item, ...(n.read_at ? {} : styles.itemUnread) }}>
                  <span style={{ ...styles.itemIcon, ...(n.type === 'invoice_overdue' || n.data?.overdue ? styles.iconWarn : {}) }}>
                    <Icon name={icon} size={15} />
                  </span>
                  <span style={styles.itemText}>
                    <span style={{ ...styles.itemTitle, fontWeight: n.read_at ? 600 : 800 }}>{title}</span>
                    {body && <span style={styles.itemBody}>{body}</span>}
                    <span style={styles.itemTime}>{timeAgo(n.created_at, tr)}</span>
                  </span>
                  {!n.read_at && <span style={styles.dot} />}
                </button>
              );
            })}
          </div>
          <button onClick={() => { setOpen(false); navigate('/podesavanja/notifikacije'); }} style={styles.footerBtn}>
            <Icon name="pencil" size={14} /> {tr('Podešavanja obaveštenja', 'Notification settings', 'Настройки уведомлений')}
          </button>
        </div>
      )}

      <style>{`
        /* Telefon i tablet: lista ravnomerno od ivica ekrana, ispod gornje trake i iznad donje trake */
        @media (max-width: 1023px) {
          .notif-panel {
            position: fixed !important;
            left: 12px !important;
            right: 12px !important;
            top: calc(70px + env(safe-area-inset-top, 0px)) !important;
            width: auto !important;
            display: flex;
            flex-direction: column;
            max-height: calc(100vh - 70px - 96px - env(safe-area-inset-bottom, 0px));
            max-height: calc(100dvh - 70px - 96px - env(safe-area-inset-bottom, 0px));
          }
          .notif-list { max-height: none !important; flex: 1 1 auto; min-height: 0; -webkit-overflow-scrolling: touch; }
          body.notif-open .assistant-fab { display: none !important; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  bellBtn: { position: 'relative', width: 40, height: 40, borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink-700)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  badge: { position: 'absolute', top: -5, right: -5, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, background: 'var(--red-600)', color: '#fff', fontSize: 10.5, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--surface)' },
  panel: { position: 'absolute', right: 0, top: 48, width: 'min(380px, calc(100vw - 24px))', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, boxShadow: 'var(--shadow-lg)', zIndex: 50, overflow: 'hidden' },
  panelHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: '1px solid var(--line)', fontSize: 14, color: 'var(--ink-900)' },
  linkBtn: { border: 'none', background: 'none', color: 'var(--teal-700)', fontWeight: 700, fontSize: 12, padding: 0 },
  list: { maxHeight: 420, overflowY: 'auto' },
  empty: { padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--ink-500)' },
  item: { display: 'flex', alignItems: 'flex-start', gap: 10, width: '100%', textAlign: 'left', border: 'none', borderBottom: '1px solid var(--line)', background: 'var(--surface)', padding: '11px 14px', cursor: 'pointer' },
  itemUnread: { background: 'var(--lavender-100)' },
  itemIcon: { width: 30, height: 30, borderRadius: 9, background: 'var(--teal-100)', color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  iconWarn: { background: 'var(--gold-100)', color: 'var(--gold-600)' },
  itemText: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  itemTitle: { fontSize: 13, color: 'var(--ink-900)', lineHeight: 1.35 },
  itemBody: { fontSize: 12, color: 'var(--ink-700)', lineHeight: 1.4 },
  itemTime: { fontSize: 11, color: 'var(--ink-500)' },
  dot: { width: 8, height: 8, borderRadius: '50%', background: 'var(--lavender-700)', marginTop: 6, flexShrink: 0 },
  footerBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', border: 'none', background: 'var(--paper)', color: 'var(--teal-800)', fontWeight: 700, fontSize: 13, padding: '11px 0' },
};
